import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Search, Loader2, Bike, Store, Clock, CheckCircle2, AlertCircle, ArrowRight, LocateFixed } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";

function SuggestCard({ b, pc, onChoose, tone = "green" }) {
  return (
    <button data-testid={`suggest-branch-card-${b.id}`} onClick={() => onChoose(b)}
      className={`w-full text-left rounded-2xl border-2 transition-colors p-4 group ${
        tone === "green" ? "border-metros-red/40 bg-metros-red/5 hover:bg-metros-red/10 hover:border-metros-red"
          : "border-gray-200 bg-gray-50 hover:border-metros-red"}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <MapPin size={18} className="text-metros-red" />
        <h3 className="font-heading font-extrabold text-lg">{b.name.replace("METROS — ", "")}</h3>
        {b.is_open === true ? (
          <span data-testid={`suggest-open-badge-${b.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-green-700 bg-green-100 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500" /> Open{b.closes_at ? ` · till ${b.closes_at}` : ""}
          </span>
        ) : (
          <span data-testid={`suggest-closed-badge-${b.id}`} className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">
            <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> Closed{b.opens_at ? ` · opens ${b.opens_at}` : ""}
          </span>
        )}
      </div>
      {b.address && <p className="text-sm text-muted-foreground mt-1">{b.address}</p>}

      <div className="flex flex-wrap items-center gap-2 mt-3">
        {b.delivers ? (
          <span data-testid={`suggest-delivery-badge-${b.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
            <Bike size={13} /> Delivery available
          </span>
        ) : (
          <span data-testid={`suggest-nodelivery-badge-${b.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-metros-red bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
            <Bike size={13} /> Outside delivery area
          </span>
        )}
        <span data-testid={`suggest-collection-badge-${b.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
          <Store size={13} /> Collection available
        </span>
        {b.delivers && b.estimated_time && (
          <span data-testid={`suggest-eta-badge-${b.id}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-metros-black bg-gray-100 px-2.5 py-1 rounded-full">
            <Clock size={13} className="text-metros-red" /> {b.estimated_time}
          </span>
        )}
        {b.distance != null && (
          <span data-testid={`suggest-distance-${b.id}`} className="text-xs text-muted-foreground">{b.distance} mi away</span>
        )}
      </div>

      <span className="inline-flex items-center gap-1.5 mt-3 text-metros-red font-bold text-sm">
        <CheckCircle2 size={15} /> Start your order from this branch <ArrowRight size={15} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
}

export default function BranchSuggestBar() {
  const { setSelectedBranch, setPostcode, setOrderType, setScheduleIntent } = useCart();
  const navigate = useNavigate();
  const [pc, setPc] = useState("");
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [locError, setLocError] = useState("");
  const [result, setResult] = useState(null); // { delivering:[], nearby }
  const [invalid, setInvalid] = useState(false);

  const runCheck = async (postcode) => {
    const q = (postcode || "").trim();
    if (!q) return;
    setBusy(true);
    setInvalid(false);
    setResult(null);
    try {
      const { data } = await api.get(`/branch/for-postcode?postcode=${encodeURIComponent(q)}`);
      if (!data.valid_postcode) { setInvalid(true); return; }
      const branches = data.branches || [];
      // all branches that can deliver, closest (and open) first
      const delivering = branches
        .filter((b) => b.delivers)
        .sort((a, b) => ((b.is_open ? 1 : 0) - (a.is_open ? 1 : 0)) || ((a.distance ?? 9999) - (b.distance ?? 9999)));
      let nearby = null;
      if (!delivering.length) {
        const withDist = [...branches].sort((a, b) => (a.distance ?? 9999) - (b.distance ?? 9999));
        nearby = withDist[0] || null;
      }
      setResult({ delivering, nearby });
    } catch {
      setInvalid(true);
    } finally { setBusy(false); }
  };

  const check = async (e) => {
    e?.preventDefault();
    await runCheck(pc);
  };

  const locateMe = () => {
    setLocError("");
    setInvalid(false);
    if (!navigator.geolocation) { setLocError("Location isn't supported on this device."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { latitude, longitude } = pos.coords;
          const { data } = await api.get(`/branch/reverse-geocode?lat=${latitude}&lon=${longitude}`);
          if (data.postcode) {
            const found = data.postcode.toUpperCase();
            setPc(found);
            await runCheck(found);
          } else {
            setLocError("We couldn't find a postcode for your location. Please type it in.");
          }
        } catch {
          setLocError("Something went wrong finding your postcode. Please type it in.");
        } finally { setLocating(false); }
      },
      () => {
        setLocating(false);
        setLocError("We couldn't access your location. Please allow it or type your postcode.");
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
    );
  };

  const startOrder = (b) => {
    setScheduleIntent(false);
    setSelectedBranch({ id: b.id, name: b.name });
    setPostcode(pc.trim().toUpperCase());
    setOrderType(b.delivers ? "delivery" : "collection");
    navigate("/menu");
  };

  const multiple = result?.delivering?.length > 1;

  return (
    <div className="relative z-30 max-w-3xl mx-auto px-4 md:px-6 -mt-3 md:-mt-10" data-testid="branch-suggest-bar">
      <div className="bg-white rounded-2xl shadow-xl ring-1 ring-black/5 p-2 md:p-4">
        <form onSubmit={check} className="flex items-center gap-1.5 md:gap-2">
          <div className="relative flex-1">
            <MapPin size={15} className="absolute left-2.5 md:left-3 top-1/2 -translate-y-1/2 text-metros-red" />
            <input data-testid="suggest-postcode-input" value={pc}
              onChange={(e) => { setPc(e.target.value.toUpperCase()); setInvalid(false); }}
              placeholder="Enter your postcode e.g. KT19 9UR"
              className="w-full rounded-lg md:rounded-xl bg-gray-100 pl-8 md:pl-10 pr-3 py-1.5 md:py-3 text-xs md:text-sm uppercase focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </div>
          <button data-testid="suggest-check-btn" type="submit" disabled={busy || !pc.trim()}
            className="shrink-0 bg-metros-red hover:bg-metros-redhover text-white rounded-lg md:rounded-xl px-3 md:px-6 py-1.5 md:py-3 text-xs md:text-base font-bold flex items-center gap-2 disabled:opacity-60 active:scale-95 transition-all">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <><Search size={15} /><span className="hidden md:inline">Find branch</span></>}
          </button>
          <button data-testid="suggest-locate-btn" type="button" onClick={locateMe} disabled={locating || busy}
            className="shrink-0 inline-flex items-center gap-1 text-xs md:text-sm font-semibold text-metros-red hover:text-metros-redhover disabled:opacity-60 active:scale-95 transition-all px-1">
            {locating ? <Loader2 className="animate-spin" size={14} /> : <LocateFixed size={14} />}
            <span>{locating ? "Locating…" : "Use my location"}</span>
          </button>
        </form>

        {locError && (
          <p data-testid="suggest-loc-error" className="text-sm text-metros-red mt-2 flex items-center gap-1.5">
            <AlertCircle size={15} /> {locError}
          </p>
        )}

        {invalid && (
          <p data-testid="suggest-invalid" className="text-sm text-metros-red mt-3 flex items-center gap-1.5">
            <AlertCircle size={15} /> We couldn't find that postcode. Please check it and try again.
          </p>
        )}

        {result && result.delivering.length > 0 && (
          <div className="mt-3 space-y-2.5" data-testid="suggest-delivering-list">
            <p className="text-sm font-semibold text-green-700 flex items-center gap-1.5">
              <CheckCircle2 size={16} />
              {multiple
                ? `${result.delivering.length} branches deliver to ${pc.trim()} — pick one`
                : `We deliver to ${pc.trim()}`}
            </p>
            {result.delivering.map((b) => (
              <SuggestCard key={b.id} b={b} pc={pc} onChoose={startOrder} tone="green" />
            ))}
          </div>
        )}

        {result && result.delivering.length === 0 && result.nearby && (
          <div className="mt-3 space-y-2.5">
            <p data-testid="suggest-outside" className="text-sm font-semibold text-metros-red flex items-center gap-1.5">
              <AlertCircle size={16} /> {pc.trim()} is outside our delivery area — here's your nearest branch for collection.
            </p>
            <SuggestCard b={result.nearby} pc={pc} onChoose={startOrder} tone="red" />
          </div>
        )}
      </div>
    </div>
  );
}
